/**
 * @component ry-image-comparer
 * @category  display
 * @summary   Slide a vertical divider across two layered images to compare a "before" and "after". Drag the handle with pointer/touch, or focus it and use arrow keys; the `after` image is revealed via a clip-path driven by `position` (0–100%).
 *
 * Ported from Shoelace's `<sl-image-comparer>` (MIT) —
 * https://github.com/shoelace-style/shoelace/blob/next/src/components/image-comparer/image-comparer.component.ts
 * Visual model also informed by daisyUI's `diff` component (MIT) —
 * https://github.com/saadeghi/daisyui/blob/master/packages/daisyui/src/components/diff.css
 *
 * Two images are stacked in the same box. The "before" image fills the box;
 * the "after" image is layered on top and clipped from the right edge so only
 * the left `position`% of it is visible. A draggable divider sits at the seam.
 * The reveal technique is Shoelace's verbatim: `clip-path: inset(0 (100-pos)% 0 0)`
 * on the after layer, with the divider's `left` at `position%`.
 *
 * @attribute position   number   default=50   Divider position as a percentage (0–100). 0 shows the before image fully; 100 shows the after image fully. Reflected to the attribute and clamped to [0,100] on set.
 *
 * @property  position   number   Live numeric divider position, clamped to [0,100] on set. Setting it re-renders the clip + divider and fires `ry:change`.
 *
 * @event ry:change
 *        detail: { position: number }
 *        fires:  on every position change — each drag micro-step, each keyboard step, and each programmatic `.position =` set. There is no separate "commit" event; for debounced commits, throttle on your side.
 *
 * @keyboard  Only when the handle is focused (it is `tabindex="0"`). ArrowLeft — move divider toward before (−1, or −10 with Shift). ArrowRight — move toward after (+1, or +10 with Shift). Home — jump to 0. End — jump to 100.
 *
 * @content   Two images mapped to before/after. Use `slot="before"` / `slot="after"` on `<img>`/`<svg>` (Shoelace model), OR provide exactly two un-slotted children — the FIRST becomes before, the SECOND becomes after. Slotted children win over positional mapping.
 *
 * @dom
 *   ry-image-comparer[position]
 *     > div.ry-image-comparer__base       [data-ry-target="base"]
 *       > div.ry-image-comparer__before   [data-ry-target="before"]   ← {before image}
 *       > div.ry-image-comparer__after    [data-ry-target="after"]    style="clip-path: inset(0 X% 0 0)"   ← {after image}
 *       > div.ry-image-comparer__divider  [data-ry-target="divider"]  style="left: position%"
 *         > div.ry-image-comparer__handle [data-ry-target="handle"]   role="slider"  tabindex="0"  aria-valuenow/min/max  aria-label
 *
 * @gotcha  `role="slider"` (NOT Shoelace's legacy `role="scrollbar"`). ry-ui standardizes draggable value handles on `role="slider"` with `aria-valuenow/min/max` to match `ry-slider`. If you port assistive-tech expectations from Shoelace, note this deliberate difference.
 *
 * @gotcha  The reveal is a clip on the AFTER layer, not a width change — both images keep their natural size, so they MUST be the same dimensions or the seam won't line up. Set equal `width`/`height` (or `object-fit: cover` + a fixed host height) on both images.
 *
 * @gotcha  Children are read ONCE in `setup()` and moved into the before/after layers. Appending images later (after upgrade) does NOT re-map them. Populate both images in the initial markup, or replace the whole `<ry-image-comparer>…</ry-image-comparer>` block.
 *
 * @gotcha  Positional fallback requires EXACTLY two un-slotted children. If you mix one `slot="before"` with one un-slotted child, only the slotted one is mapped and the other is left as-is. Either slot both or slot neither.
 *
 * @gotcha  Drag uses real layout (`getBoundingClientRect`) and is NOT observable in happy-dom — Vitest covers the keyboard + programmatic + clip-output paths. Cover drag interactively / with Playwright.
 *
 * @example basic
 * ```html
 * <ry-image-comparer>
 *   <img slot="before" src="https://placehold.co/600x400/222/fff" alt="Before">
 *   <img slot="after" src="https://placehold.co/600x400/eee/000" alt="After">
 * </ry-image-comparer>
 * ```
 *
 * @example custom-start-position
 * ```html
 * <ry-image-comparer position="25">
 *   <img slot="before" src="https://placehold.co/600x400/222/fff" alt="Before">
 *   <img slot="after" src="https://placehold.co/600x400/eee/000" alt="After">
 * </ry-image-comparer>
 * ```
 *
 * @example positional-children
 * ```html
 * <ry-image-comparer>
 *   <img src="https://placehold.co/600x400/222/fff" alt="Before">
 *   <img src="https://placehold.co/600x400/eee/000" alt="After">
 * </ry-image-comparer>
 * ```
 *
 * @tutorial renders-before-and-after-layers
 * ```js
 * // Two slotted images map to the before/after layers.
 * const el = mount(`
 *   <ry-image-comparer>
 *     <img slot="before" id="b" src="data:,">
 *     <img slot="after" id="a" src="data:,">
 *   </ry-image-comparer>
 * `);
 * const before = el.querySelector('[data-ry-target="before"]');
 * const after = el.querySelector('[data-ry-target="after"]');
 * await expectEqual(!!before.querySelector('#b'), true, 'before layer holds before image');
 * await expectEqual(!!after.querySelector('#a'), true, 'after layer holds after image');
 * ```
 *
 * @tutorial positional-children-map-to-before-after
 * ```js
 * // Wrong assumption: you must use slot="before"/"after". Two un-slotted
 * // children also work — first is before, second is after.
 * const el = mount(`
 *   <ry-image-comparer>
 *     <img id="first" src="data:,">
 *     <img id="second" src="data:,">
 *   </ry-image-comparer>
 * `);
 * await expectEqual(!!el.querySelector('[data-ry-target="before"] #first'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="after"] #second'), true);
 * ```
 *
 * @tutorial position-attribute-drives-divider-and-clip
 * ```js
 * const el = mount(`
 *   <ry-image-comparer position="30">
 *     <img slot="before" src="data:,">
 *     <img slot="after" src="data:,">
 *   </ry-image-comparer>
 * `);
 * const divider = el.querySelector('[data-ry-target="divider"]');
 * const after = el.querySelector('[data-ry-target="after"]');
 * await expectEqual(divider.style.left, '30%', 'divider sits at position%');
 * // after layer clipped from the right by (100 - position)%
 * await expectEqual(after.style.clipPath, 'inset(0 70% 0 0)', 'clip reveals left 30%');
 * ```
 *
 * @tutorial handle-has-slider-aria
 * ```js
 * const el = mount(`
 *   <ry-image-comparer position="40">
 *     <img slot="before" src="data:,">
 *     <img slot="after" src="data:,">
 *   </ry-image-comparer>
 * `);
 * const handle = el.querySelector('[data-ry-target="handle"]');
 * await expectAttr(handle, 'role', 'slider');
 * await expectAttr(handle, 'tabindex', '0');
 * await expectAttr(handle, 'aria-valuenow', '40');
 * await expectAttr(handle, 'aria-valuemin', '0');
 * await expectAttr(handle, 'aria-valuemax', '100');
 * ```
 *
 * @tutorial keyboard-arrows-move-divider
 * ```js
 * const el = mount(`
 *   <ry-image-comparer position="50">
 *     <img slot="before" src="data:,">
 *     <img slot="after" src="data:,">
 *   </ry-image-comparer>
 * `);
 * const handle = el.querySelector('[data-ry-target="handle"]');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.position));
 * handle.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.position, 51, 'ArrowRight nudges toward after by 1');
 * handle.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowLeft', bubbles: true }));
 * await expectEqual(el.position, 50, 'ArrowLeft nudges toward before by 1');
 * await expectEqual(fired[0], 51, 'ry:change fires with new position');
 * ```
 *
 * @tutorial shift-arrow-steps-by-ten
 * ```js
 * const el = mount(`
 *   <ry-image-comparer position="50">
 *     <img slot="before" src="data:,">
 *     <img slot="after" src="data:,">
 *   </ry-image-comparer>
 * `);
 * const handle = el.querySelector('[data-ry-target="handle"]');
 * handle.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', shiftKey: true, bubbles: true }));
 * await expectEqual(el.position, 60, 'Shift+Arrow steps by 10');
 * ```
 *
 * @tutorial home-and-end-jump-to-extremes
 * ```js
 * const el = mount(`
 *   <ry-image-comparer position="50">
 *     <img slot="before" src="data:,">
 *     <img slot="after" src="data:,">
 *   </ry-image-comparer>
 * `);
 * const handle = el.querySelector('[data-ry-target="handle"]');
 * handle.dispatchEvent(new KeyboardEvent('keydown', { key: 'End', bubbles: true }));
 * await expectEqual(el.position, 100, 'End jumps to 100');
 * handle.dispatchEvent(new KeyboardEvent('keydown', { key: 'Home', bubbles: true }));
 * await expectEqual(el.position, 0, 'Home jumps to 0');
 * ```
 *
 * @tutorial position-setter-clamps-0-to-100
 * ```js
 * const el = mount(`
 *   <ry-image-comparer position="50">
 *     <img slot="before" src="data:,">
 *     <img slot="after" src="data:,">
 *   </ry-image-comparer>
 * `);
 * el.position = 999;
 * await expectEqual(el.position, 100, 'clamped to 100');
 * el.position = -50;
 * await expectEqual(el.position, 0, 'clamped to 0');
 * ```
 *
 * @see ry-slider
 * @see ry-split
 */

import { RyElement } from '../core/ry-element.js';

// Note: `ry:change` is already declared on HTMLElementEventMap in
// src/ts/types.ts (with the shared ChangeDetail shape). We emit
// `{ position: number }` as the detail; do not redeclare the map here.

export class RyImageComparer extends RyElement {
  #base: HTMLElement | null = null;
  #after: HTMLElement | null = null;
  #divider: HTMLElement | null = null;
  #handle: HTMLElement | null = null;
  #dragging = false;

  static observedAttributes = ['position'] as const;

  setup(): void {
    this.#build();
    this.#bind();
    this.#update();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'position' && this.#after) {
      this.#update();
    }
  }

  // ─── Markup ────────────────────────────────────────────────────────
  #build(): void {
    // Resolve before/after sources from the light-DOM children, BEFORE we
    // wipe innerHTML. Slotted children win; otherwise fall back to the first
    // two un-slotted element children (Shoelace's before/after model).
    const slotBefore = this.querySelector<HTMLElement>(':scope > [slot="before"]');
    const slotAfter = this.querySelector<HTMLElement>(':scope > [slot="after"]');

    let beforeEl: HTMLElement | null = slotBefore;
    let afterEl: HTMLElement | null = slotAfter;

    if (!beforeEl && !afterEl) {
      const kids = [...this.children].filter(
        (c): c is HTMLElement => c instanceof HTMLElement && !c.hasAttribute('slot'),
      );
      beforeEl = kids[0] ?? null;
      afterEl = kids[1] ?? null;
    }

    this.innerHTML = `
      <div data-ry-target="base" class="ry-image-comparer__base">
        <div data-ry-target="before" class="ry-image-comparer__before"></div>
        <div data-ry-target="after" class="ry-image-comparer__after"></div>
        <div data-ry-target="divider" class="ry-image-comparer__divider">
          <div data-ry-target="handle" class="ry-image-comparer__handle"
               role="slider" tabindex="0"
               aria-label="Comparison divider"
               aria-valuemin="0" aria-valuemax="100"></div>
        </div>
      </div>
    `;

    this.#base = this.$('[data-ry-target="base"]');
    this.#after = this.$('[data-ry-target="after"]');
    this.#divider = this.$('[data-ry-target="divider"]');
    this.#handle = this.$('[data-ry-target="handle"]');

    const beforeLayer = this.$('[data-ry-target="before"]');
    const afterLayer = this.#after;
    if (beforeEl && beforeLayer) {
      beforeEl.removeAttribute('slot');
      beforeLayer.appendChild(beforeEl);
    }
    if (afterEl && afterLayer) {
      afterEl.removeAttribute('slot');
      afterLayer.appendChild(afterEl);
    }
  }

  #bind(): void {
    if (this.#divider) {
      this.on(this.#divider, 'pointerdown', this.#onPointerDown);
    }
    if (this.#handle) {
      this.on(this.#handle, 'keydown', this.#onKeyDown);
    }
  }

  // ─── Pointer drag (Shoelace's drag(base) technique) ─────────────────
  #onPointerDown = (e: PointerEvent): void => {
    if (!this.#base) return;
    e.preventDefault();
    this.#dragging = true;
    this.setAttribute('data-ry-dragging', '');
    try {
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    } catch {
      /* setPointerCapture unavailable (e.g. happy-dom) */
    }
    this.#moveFromPointer(e.clientX);
  };

  #onPointerMove = (e: PointerEvent): void => {
    if (!this.#dragging) return;
    this.#moveFromPointer(e.clientX);
  };

  #onPointerUp = (): void => {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.removeAttribute('data-ry-dragging');
  };

  #moveFromPointer(clientX: number): void {
    if (!this.#base) return;
    const { left, width } = this.#base.getBoundingClientRect();
    if (width === 0) return;
    const pct = ((clientX - left) / width) * 100;
    this.position = parseFloat(this.#clamp(pct).toFixed(2));
  }

  // ─── Keyboard (Shoelace parity: 1, Shift=10, Home/End) ──────────────
  #onKeyDown = (e: KeyboardEvent): void => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const incr = e.shiftKey ? 10 : 1;
    let next = this.position;
    if (e.key === 'ArrowLeft') next -= incr;
    if (e.key === 'ArrowRight') next += incr;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = 100;
    this.position = this.#clamp(next);
  };

  // ─── Render ─────────────────────────────────────────────────────────
  #update(): void {
    const pos = this.position;
    // Shoelace clip technique: reveal left `pos`% of the after layer.
    if (this.#after) {
      this.#after.style.clipPath = `inset(0 ${100 - pos}% 0 0)`;
    }
    if (this.#divider) {
      this.#divider.style.left = `${pos}%`;
    }
    if (this.#handle) {
      this.#handle.setAttribute('aria-valuenow', String(Math.round(pos)));
    }
    this.style.setProperty('--ry-image-comparer-position', `${pos}%`);
  }

  #clamp(v: number): number {
    return Math.max(0, Math.min(100, v));
  }

  // ─── Document drag listeners ────────────────────────────────────────
  connectedCallback(): void {
    super.connectedCallback();
    this.on(document, 'pointermove', this.#onPointerMove as EventListener);
    this.on(document, 'pointerup', this.#onPointerUp as EventListener);
  }

  // ─── Public API ─────────────────────────────────────────────────────
  get position(): number {
    return this.#clamp(parseFloat(this.getAttribute('position') ?? '50'));
  }
  set position(val: number) {
    const clamped = this.#clamp(val);
    const prev = this.getAttribute('position');
    this.setAttribute('position', String(clamped));
    this.#update();
    // Only emit when the resolved value actually changed (Shoelace fires on
    // every position write after first update; we dedupe identical writes).
    if (prev === null || parseFloat(prev) !== clamped) {
      this.emit('change', { position: clamped });
    }
  }
}

customElements.define('ry-image-comparer', RyImageComparer);
